import { z } from 'zod';
import { isValidDiscriminator, NAME_MAX_LENGTH, NAME_MIN_LENGTH } from '../guild-name.js';
/**
 * Контракт создания гильдии.
 *
 * Источник: docs/01-функции/01-первый-запуск/реализация.md
 */
/** Номер после «#»: его выбирает игрок, сервер проверяет, что он свободен. */
export const DiscriminatorSchema = z
    .string()
    .refine(isValidDiscriminator, { message: 'DISCRIMINATOR_INVALID' });
export const CreateGuildSchema = z.object({
    /**
     * Длина проверяется по символам, а не по .length: эмодзи занимает
     * две единицы UTF-16, и «🔥🔥» прошло бы как четыре символа.
     */
    name: z
        .string()
        .trim()
        .refine((v) => Array.from(v).length >= NAME_MIN_LENGTH, { message: 'NAME_TOO_SHORT' })
        .refine((v) => Array.from(v).length <= NAME_MAX_LENGTH, { message: 'NAME_TOO_LONG' }),
    discriminator: DiscriminatorSchema,
    /** null — игрок пропустил загрузку, клиент рисует букву названия. */
    logoKey: z.string().max(128).nullable(),
    frameId: z.string().min(1).max(64),
});
/**
 * Ответ проверки «имя#номер».
 *
 * `available: false` — пара занята, `suggestion` — свободный номер для
 * кнопки «Подобрать свободный». Подсказка — не бронь: её может занять
 * другой игрок до создания.
 */
export const NameCheckResponseSchema = z.object({
    available: z.boolean(),
    suggestion: z.string().length(4).nullable(),
});
export const GuildSchema = z.object({
    id: z.string().uuid(),
    telegramId: z.number().int(),
    name: z.string(),
    /** Четыре цифры: «4821». Показывается как «Рассвет#4821». */
    discriminator: z.string().length(4),
    /** Для поиска дублей. Игроку не показывается. */
    nameNormalized: z.string(),
    logoKey: z.string().nullable(),
    /** Готов к показу: либо путь к загруженному файлу, либо пусто для герба. */
    logoUrl: z.string().nullable(),
    frameId: z.string(),
    /** Совместимость с существующими аккаунтами; прогрессия уровня удалена. */
    level: z.number().int().min(1).max(80),
    createdAt: z.string(),
    nameChangedAt: z.string().nullable(),
});
/**
 * Шаг создания гильдии.
 *
 * Состояние живёт на сервере, а не в клиенте: закрытое приложение
 * и смена устройства не должны терять прогресс.
 */
export const OnboardingStepSchema = z.enum(['name', 'done']);
/**
 * Ответ на вход: один запрос вместо трёх.
 *
 * Клиент узнаёт свой шаг, гильдию и каталог оформления разом —
 * на медленной сети три последовательных запроса дают заметную паузу.
 */
/**
 * Существующие балансы билетов аккаунта. Механики расходования не подключены.
 */
export const ResourcesSchema = z.object({
    ticketsStandard: z.number().int().nonnegative(),
    ticketsEvent: z.number().int().nonnegative(),
});
export const BootstrapResponseSchema = z.object({
    step: OnboardingStepSchema,
    guild: GuildSchema.nullable(),
    /** null, пока гильдия не создана. */
    resources: ResourcesSchema.nullable(),
    /** Хеш пакета баланса: клиент качает каталог, только если он сменился. */
    balanceHash: z.string(),
});
export const CreateGuildResponseSchema = z.object({
    guild: GuildSchema,
    /** Стартовые билеты найма. Источник: реализация.md, транзакция создания. */
    ticketsStandard: z.number().int().nonnegative(),
});
export const UploadLogoResponseSchema = z.object({
    logoKey: z.string(),
    logoUrl: z.string(),
});
/** Лимиты загрузки логотипа. Клиент сжимает до отправки. */
export const LOGO_MAX_BYTES = 64 * 1024;
export const LOGO_SIZE_PX = 256;
//# sourceMappingURL=guild.js.map